import useEventListener from '@use-it/event-listener';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { allow } from '../../common/classes/allow';
import local from '../../common/classes/local';
import ApiErrorAlert from '../../common/components/api.error.alert';
import Button from '../../common/components/button';
import DateFields from '../../common/components/date.fields';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import H2 from '../../common/components/h2';
import H5 from '../../common/components/h5';
import H6 from '../../common/components/h6';
import InputField from '../../common/components/input.field';
import MedicareNumberDialog from '../../common/components/medicare.number.dialog';
import MedicareNumberLabel from '../../common/components/medicare.number.label';
import MedicareStartDateDialog from '../../common/components/medicare.start.date.dialog';
import { PrivacyActDialog } from '../../common/components/privacy.act.dialog';
import StackingButtons from '../../common/components/stacking.buttons';
import SubHeading from '../../common/components/sub.heading.section';
import TranslatedSpan from '../../common/components/translated.span';
import ValidationAlertMessage from '../../common/components/validation.alert.message';
import ValidationAlerts from '../../common/components/validation.alerts';
import capitalizeFirstLetter from '../../common/functions/capitalize.first.letter';
import { isValidMedicareNumber } from '../../common/functions/is.valid.medicare.number';
import { isValidYear } from '../../common/functions/is.valid.year';
import translate, { TranslationString } from '../../common/functions/translate';
import { useConversion, useFormStart } from '../../common/hooks/use.analytics';
import useTrait from '../../common/hooks/use.trait';
import useViewport from '../../common/hooks/use.viewport';
import is from '../../common/objects/is';
import dateFieldValue from '../../common/objects/models/date.field.value';
import the from '../../common/objects/the';
import use from '../../common/objects/use';

const YourMedicareInformation = () => {
    const [dayRef, setDayRef] = useState();
    const [medicareNumberRef, setMedicareNumberRef] = useState();
    const [monthRef, setMonthRef] = useState();
    const [yearRef, setYearRef] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const apiErrorMessage = useTrait<TranslationString | null>(null);
    const dateFieldKey = useTrait(new Date().getTime());
    const history = useHistory();
    const medicareNumberDialogIsOpen = useTrait(false);
    const medicareNumberError = useTrait(false);
    const medicareNumErrorMessage = useTrait<TranslationString | null>(null);
    const medicareStartDateDialogIsOpen = useTrait(false);
    const validateEntireForm = useTrait(false);
    const {width} = useViewport();

    const api = use.createAccountApi!;
    const data = use.createAccountData!;
    const day = translate('day');
    const month = translate('month');
    const year = translate('year');

    const formStart = useFormStart('sign_up');
    const conversion = useConversion('create account', 'sign_up');


    const callCreateAccount = async () => {
        const response = await api.step1(data.medicareNumber.get(), data.coverageStartDate.get(), data.part.get());
        setIsLoading(false);
        const statusCode = response?.status;
        validateEntireForm.set(false);
        dateFieldKey.set(new Date().getTime());

        let success = false;
        const notFoundError = 'We’re not able to find a matching record for the information you provided. Please check the information and try again.';
        switch (statusCode) {
                case 204:
                    success = true;
                    data.stepForward();
                    break;
                case 400:
                    reset();
                    const didNotMatch = response?.data?.errors?.some((error: any) => error.message?.includes('did not match'));
                    const invalidMBI = response?.data?.errors?.some((error: any) => error.name === 'InvalidField' && error.field === 'mbi');
                    if (didNotMatch) {
                        apiErrorMessage.set(notFoundError);
                    } else if (invalidMBI) {
                        apiErrorMessage.set('Please check your Medicare Number.');
                    } else {
                        apiErrorMessage.set('We couldn\'t process your request. Please re-enter your information and try again.');
                    }
                    break;
                case 404:
                    reset();
                    apiErrorMessage.set(notFoundError);
                    break;
                case 409:
                    reset();
                    apiErrorMessage.set('It looks like you already have an account.');
                    break;
                default:
                    reset();
                    apiErrorMessage.set('We can\'t process your request at this time. We\'re sorry for any inconvenience. Please try again.');
                    break;
        }

        if (!success) {
            if (response?.data) {
                const errorMessage = apiErrorMessage.get() || 'unkown error';
                conversion.failAPIIntermediate(errorMessage);
            } else {
                conversion.failAPIIntermediate('unknown error');
            }
        }
    };

    const getApiErrorMessage = () => {
        const error = apiErrorMessage.get();
        if (!error)
            return null;
        return translate(error);
    };

    const getCancelButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'outlined'}
                onClick={() => history.push('/login')}
                size={'small'}
            >
                <TranslatedSpan english={'Cancel'}/>
            </Button>
        </>;
    };

    const getNextButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'solid'}
                onClick={handleNextButtonClick}
                size={'small'}
                isLoading={isLoading}
            >
                <TranslatedSpan english={'Next'}/>
            </Button>
        </>;
    };

    const getValidationAlertMessages = () => {
        const messages: JSX.Element[] = [];
        if (!validateEntireForm.get())
            return messages;
        const mbi = data.medicareNumber.get();
        if (mbi === '') {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter your Medicare Number.')}
                    fieldReference={medicareNumberRef}
                    linkText={translate('Medicare Number')}
                />
            );
        } else if (!isValidMedicareNumber(mbi)) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Please check your Medicare Number.')}
                    fieldReference={medicareNumberRef}
                    linkText={translate('Medicare Number')}
                />
            );
        }

        if (data.coverageStartDate.get().month === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} your Medicare coverage started.', {timePeriod: month})}
                    fieldReference={monthRef}
                    linkText={capitalizeFirstLetter(month)}
                />
            );
        if (data.coverageStartDate.get().day === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} your Medicare coverage started.', {timePeriod: day})}
                    fieldReference={dayRef}
                    linkText={capitalizeFirstLetter(day)}
                />
            );
        if (!isValidYear(data.coverageStartDate.get().year))
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} your Medicare coverage started.', {timePeriod: year})}
                    fieldReference={yearRef}
                    linkText={capitalizeFirstLetter(year)}
                />
            );
        return messages;
    };

    const handleNextButtonClick = () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);

        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef != null) {
            errorFieldRef.focus();
            setIsLoading(false);
            return;
        }
        callCreateAccount();
    };

    const reset = () => {
        apiErrorMessage.set(null);
    };

    const updateCoverageStartDate = (dateObject: {day: string, month: string, year: string}) => {
        allow.anInstanceOf(dateObject, dateFieldValue);
        formStart();
        data.coverageStartDate.set(dateObject);
        if (validateEntireForm.get())
            validateForm();
    };

    const updateMedicareNumber = (value = '', doImmediateValidation = false) => {
        allow.aString(value).aBoolean(doImmediateValidation);
        formStart();
        const newValue = value.trim();
        data.medicareNumber.set(newValue);
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateMedicareNumber();
        if (validateEntireForm.get())
            validateForm();
    };

    const updatePart = () => {
        formStart();
        const newPart = data.part.get() === 'A' ? 'B' : 'A';
        local.setItem('part', newPart);
        data.part.set(newPart);
    };

    const validateForm = (): HTMLElement | null | undefined => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (!validateMedicareNumber())
            errorFieldRef = medicareNumberRef;
        if (data.coverageStartDate.get().month === '' && !errorFieldRef)
            errorFieldRef = monthRef;
        if (data.coverageStartDate.get().day === '' && !errorFieldRef)
            errorFieldRef = dayRef;
        if (!isValidYear(data.coverageStartDate.get().year) && !errorFieldRef)
            errorFieldRef = yearRef;
        const messages = getValidationAlertMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidationIntermediate(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };

    const validateMedicareNumber = () => {
        const mbi = data.medicareNumber.get();
        const isPresent = !!mbi;
        const isValid = isValidMedicareNumber(mbi);

        if (!isPresent) {
            medicareNumErrorMessage.set('Enter your Medicare Number.');
        } else {
            medicareNumErrorMessage.set('Please check your Medicare Number.');
        }

        if (medicareNumberError.get() === isValid)
            medicareNumberError.set(!isValid);
        return isValid;
    };

    useEventListener('keyup', (event: any) => {
        if (event.key === 'Enter')
            handleNextButtonClick();
    });

    return <>
        <PrivacyActDialog />
        <MedicareNumberDialog
            isOpen={medicareNumberDialogIsOpen.get()}
            onExit={() => medicareNumberDialogIsOpen.set(false)}
        />
        <MedicareStartDateDialog
            isOpen={medicareStartDateDialogIsOpen.get()}
            onExit={() => medicareStartDateDialogIsOpen.set(false)}
            part={data.part.get()}
        />
        <fieldset style={{
            border: 0,
            margin: 0,
            padding: 0,
        }}>
            <Div isRow={true}>
                <Div cols={12}>
                    <H1>
                        <TranslatedSpan english={'Create an account'}/>
                    </H1>
                    <ApiErrorAlert errorMessage={getApiErrorMessage()}/>
                    <legend>
                        <Div paddingTopSM={1} paddingTopMD={2} paddingTopLG={2} paddingTopXL={4}>
                            <H6><TranslatedSpan english={'Step'}/> 1 <TranslatedSpan english={'of'}/> 3</H6>
                        </Div>
                        <H2 style={{marginTop: 8}}>
                            <TranslatedSpan english={'Your Medicare information'}/>
                        </H2>
                    </legend>
                    <SubHeading style={{fontSize: 14, fontWeight: 400}}>
                        <TranslatedSpan english={'All fields required.'}/>
                    </SubHeading>
                    <ValidationAlerts
                        heading={`${translate('Error')}: ${translate('Complete these items to sign up for an account.')}`}
                        validationMessages={getValidationAlertMessages()}
                    />
                </Div>
            </Div>
            <Div isRow={true}>
                <Div
                    cols={12} colsSM={10} colsMD={8} colsLG={6} colsXL={5}
                    paddingRightLG={0}
                >
                    <InputField
                        aria-invalid={medicareNumberError.get().toString()}
                        // @ts-ignore
                        errorMessage={medicareNumberError.get() ? `${translate('Error')}: ${translate(medicareNumErrorMessage.get())}` : null}
                        inputRef={setMedicareNumberRef}
                        label={<MedicareNumberLabel onClick={() => medicareNumberDialogIsOpen.set(true)}/>}
                        name={'medicareNumber'}
                        onClick={formStart}
                        onFocus={formStart}
                        onBlur={event => updateMedicareNumber(event.currentTarget.value, is.to.be.validated)}
                        onChange={event => updateMedicareNumber(event.currentTarget.value)}
                        style={{minWidth: '100%'}}
                        value={data.medicareNumber.get()}
                    />
                </Div>
            </Div>
            <Div isRow={true}>
                <Div cols={12}>
                    <div style={{
                        position: 'relative',
                        top: 24,
                    }}>
                        <H6 style={{color: the.color.nearBlack,}}>
                            <TranslatedSpan
                                english={'Part {{letter}} Coverage Start Date'}
                                replacements={{letter: data.part.get()}}
                            />
                        </H6>
                        <Button
                            color={'blue'}
                            displayAs={'link'}
                            onClick={() => medicareStartDateDialogIsOpen.set(true)}
                            style={{
                                fontSize: width < the.minimumMediumViewportWidth ? 14 : 16,
                                lineHeight: width < the.minimumMediumViewportWidth ? 1 : 1.3,
                                marginBottom: 10,
                                padding: 0,
                                textAlign: 'left',
                            }}
                        >
                            <TranslatedSpan
                                english={'Where can I find my Part {{letter}} start date?'}
                                replacements={{letter: data.part.get()}}
                            />
                        </Button>
                    </div>
                    <DateFields
                        date={data.coverageStartDate.get()}
                        dayErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} your Medicare coverage started.', {timePeriod: day})}`}
                        dayFieldRef={setDayRef}
                        forceValidation={validateEntireForm.get()}
                        hint={translate('Use the format MM/DD/YYYY')}
                        key={dateFieldKey.get()}
                        monthErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} your Medicare coverage started.', {timePeriod: month})}`}
                        monthFieldRef={setMonthRef}
                        onBlur={date => updateCoverageStartDate(date)}
                        onChange={date => updateCoverageStartDate(date)}
                        yearErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} your Medicare coverage started.', {timePeriod: year})}`}
                        yearFieldRef={setYearRef}
                    />
                </Div>
            </Div>
            <Div isRow={true}>
                <Div
                    cols={12}
                    paddingTop={2}
                >
                    <H5>
                        <TranslatedSpan
                            english={`Don't have Part {{letter}}?`}
                            replacements={{letter: data.part.get()}}
                        />
                    </H5>
                    <Button
                        color={'blue'}
                        displayAs={'link'}
                        onClick={updatePart}
                        style={{padding: 0}}
                    >
                        <TranslatedSpan
                            english={'Switch to Part {{letter}}'}
                            replacements={{letter: data.part.get() === 'A' ? 'B' : 'A'}}
                        />
                    </Button>
                </Div>
            </Div>
        </fieldset>
        <StackingButtons
            button1={getCancelButton()}
            button2={getNextButton()}
        />
    </>;
}

export default YourMedicareInformation;
